import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AmmoStockFullWidth = ({ defaultExpanded = true }) => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const canOpenAmmoSettings = isAdmin || isSuperAdmin;
  const [loading, setLoading] = useState(true);
  const [ammoTypes, setAmmoTypes] = useState([]);
  // Desktop default: expanded, Mobile default: collapsed
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768 ? defaultExpanded : false;
    }
    return defaultExpanded;
  });

  useEffect(() => {
    fetchAmmoStock();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchAmmoStock, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAmmoStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Only show enabled ammo types
      const enabled = response.data.ammo_types.filter(a => a.enabled);
      setAmmoTypes(enabled);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ammo stock:', error);
      setLoading(false);
    }
  };

  const getStockStatus = (ammo) => {
    const currentStock = ammo.current_stock || 0;
    
    let percentage;
    if (ammo.initial_stock && ammo.initial_stock > 0) {
      percentage = (currentStock / ammo.initial_stock) * 100;
    } else {
      if (currentStock >= 50) {
        percentage = 100;
      } else if (currentStock >= 20) {
        percentage = 35;
      } else if (currentStock >= 10) {
        percentage = 20;
      } else {
        percentage = 10;
      }
    }
    
    // Color coding: Red < 15%, Yellow 15-40%, Green > 40%
    if (percentage < 15) {
      return { color: 'text-red-400', bgColor: 'bg-red-900/20', icon: AlertTriangle, label: 'Kritisk lavt', percentage };
    } else if (percentage < 40) {
      return { color: 'text-yellow-400', bgColor: 'bg-yellow-900/20', icon: AlertTriangle, label: 'Lavt', percentage };
    } else {
      return { color: 'text-green-400', bgColor: 'bg-green-900/20', icon: Package, label: 'God beholdning', percentage };
    }
  };

  if (loading || ammoTypes.length === 0) {
    return null;
  }

  // Check if any ammo is low (under 40%)
  const hasLowStock = ammoTypes.some(ammo => {
    const status = getStockStatus(ammo);
    return status.percentage < 40;
  });

  const totalValue = Math.round(ammoTypes.reduce((sum, ammo) => sum + (ammo.current_stock * ammo.price_per_box), 0));

  return (
    <div className="bg-gray-800 rounded-lg p-4 mb-6">
      {/* Header with expand/collapse */}
      <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5" fill="#FFD700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1720.46 1622.88">
            <path d="M239.43,0C56.46,0,19.96,196.91,18.46,308.13h438.7C453.42,194.7,429.65,0,239.43,0Z"/>
            <path d="M437.17,345.42H38.43c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69H0v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
            <path d="M861.85,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
            <path d="M1059.59,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
            <path d="M1484.28,0c-182.97,0-219.47,196.91-220.98,308.13h438.7c-3.73-113.43-27.5-308.13-217.72-308.13Z"/>
            <path d="M1682.02,345.42h-398.74c-16.95,0-19.97,17.78-19.97,17.78v1181.84h66.41v16.69h-84.86v61.14h475.61v-61.14h-84.86v-16.69h66.41V363.2s-2.56-17.78-19.98-17.78Z"/>
          </svg>
          <h3 className="text-white font-semibold text-lg">Ammunisjonslager</h3>
          {hasLowStock && (
            <div className="flex items-center gap-1 text-yellow-400 text-xs ml-2">
              <AlertTriangle className="h-3 w-3" />
              <span>Påfyll nødvendig</span>
            </div>
          )}
        </div>
        <button 
          className="p-1 hover:bg-gray-700 rounded transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <>
          {/* Ammo grid - Responsive with auto-fit to fill width if less than 5 items */}
          <div className="grid gap-3 mb-3" style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gridAutoRows: '1fr'
          }}>
            {ammoTypes.map((ammo) => {
              const status = getStockStatus(ammo);
              const StatusIcon = status.icon;
              const totalRounds = ammo.current_stock * ammo.rounds_per_box;

              return (
                <div
                  key={ammo.caliber}
                  className={`p-3 rounded ${status.bgColor} flex flex-col justify-between`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm font-semibold">{ammo.caliber}</span>
                    <div className="flex items-center gap-1">
                      <StatusIcon className={`h-4 w-4 ${status.color}`} />
                      {canOpenAmmoSettings && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate('/admin?tab=settings#ammo-salg'); }}
                          className="p-0.5 opacity-70 hover:opacity-100 transition-opacity"
                          title="Legg til innkjøpt ammo (Ammo-innstillinger)"
                          data-testid={`ammo-stock-open-settings-${ammo.caliber}`}
                        >
                          <img src="/patron-symbol.svg" alt="Ammo-innstillinger" className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs mb-2">
                    <div className={status.color}>
                      <span className="font-bold text-base">{ammo.current_stock}</span> esker
                    </div>
                    <div className="text-gray-400">
                      {totalRounds.toLocaleString()} skudd
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        status.color === 'text-red-400' ? 'bg-red-400' :
                        status.color === 'text-yellow-400' ? 'bg-yellow-400' :
                        'bg-green-400'
                      }`}
                      style={{
                        width: `${Math.min(100, Math.max(0, status.percentage))}%`
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total value */}
          <div className="pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Total verdi:</span>
              <span className="text-yellow-400 font-medium text-lg">
                {totalValue.toLocaleString()} kr
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AmmoStockFullWidth;
